import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

function cls(...a) { return a.filter(Boolean).join(' '); }

function Pill({ children, tone='slate', className='' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <span className={cls('inline-flex items-center px-2 py-0.5 rounded-full border text-xs', map[tone], className)}>
      {children}
    </span>
  );
}

function disciplineToneByServiceType(serviceType) {
  const t = String(serviceType || '').toUpperCase();
  // (ako želiš 1:1 mapping sa vašim postojećim bojama, ovde samo prilagodi)
  if (t === 'EI') return 'sky';
  if (t === 'PI') return 'indigo';
  if (t === 'ABA') return 'amber';
  if (t === 'OT') return 'emerald';
  if (t === 'PT') return 'violet';
  return 'slate';
}

function Modal({ open, onClose, title, children, widthClass='max-w-4xl' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className={cls('w-full bg-white rounded-2xl shadow-xl border border-slate-200', widthClass)} onMouseDown={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-semibold text-slate-800">{title}</div>
          <button className="px-2 py-1 text-sm rounded-lg border border-slate-200 hover:bg-slate-50" onClick={onClose}>Close</button>
        </div>

        {/* ✅ scroll body */}
        <div className="p-4 max-h-[80vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type='text', placeholder='', error=false }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <input
        type={type}
        value={value || ''}
        placeholder={placeholder}
        onChange={(e)=>onChange(e.target.value)}
        className={cls(
          'w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400',
          error ? 'border-red-300 bg-red-50' : 'border-slate-300'
        )}
      />
    </label>
  );
}

function Select({ label, value, onChange, options, error=false, hint='' }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <select
        value={value || ''}
        onChange={(e)=>onChange(e.target.value)}
        className={cls(
          'w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400',
          error ? 'border-red-300 bg-red-50' : 'border-slate-300'
        )}
      >
        <option value="">—</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {hint ? <div className="mt-1 text-[11px] text-slate-500">{hint}</div> : null}
    </label>
  );
}

function remainingTone(rem) {
  if (rem <= 0) return 'red';
  if (rem <= 5) return 'amber';
  return 'emerald';
}

export default function AdminAuthorizations() {
  const ALL = '__all__';

  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(ALL);

  const [payers, setPayers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [disciplines, setDisciplines] = useState([]); // global list (for display in list, not for service-row select anymore)

  const [auths, setAuths] = useState([]);
  const [usageMap, setUsageMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [modal, setModal] = useState(false);

  const [studentDisciplines, setStudentDisciplines] = useState([]); // allowed in modal dropdown
  const [studentPrimary, setStudentPrimary] = useState(null); // {service_type, name, id}

  const [servicesError, setServicesError] = useState(false);
  const [serviceRowErrors, setServiceRowErrors] = useState({}); // {idx: {discipline_id:true}}

  const [form, setForm] = useState({
    id: null,
    student_id: null,
    plan_id: '',
    auth_number: '',
    start_date: '',
    end_date: '',
    unit_type: 'unit',
    total_units: '0',
    used_units_override: '',
    notes: '',
    status: 'active',
    services: [],
  });

  function studentLabel(s) {
    const ln = (s.last_name || '').trim();
    const fn = (s.first_name || '').trim();
    const name = `${ln}${ln ? ', ' : ''}${fn}`.trim() || `Student #${s.id}`;
    const primaryCode = s.primary_discipline_service_type || '—';
    return `${name} (${primaryCode})`;
  }

  const studentOptions = useMemo(() => {
    return [
      { value: ALL, label: 'All (with authorizations)' },
      ...students.map(s => ({
        value: String(s.id),
        label: studentLabel(s),
      })),
    ];
  }, [students]);

  const planOptions = useMemo(
    () => plans.map(pl => {
      const payer = payers.find(p => String(p.id) === String(pl.payer_id));
      return { value: String(pl.id), label: `${pl.name}${payer ? ` — ${payer.name}` : ''}` };
    }),
    [plans, payers]
  );

  const disciplineOptionsForStudent = useMemo(() => {
    // only student_disciplines (requested)
    return (studentDisciplines || []).map(d => ({
      value: String(d.discipline_id),
      label: `${d.name} (${d.service_type})${Number(d.is_primary) === 1 ? ' ★ primary' : ''}`,
    }));
  }, [studentDisciplines]);

  async function loadBase() {
    try {
      setErr('');
      setLoading(true);

      // ✅ use students-with-primary-discipline for the label in dropdown
      const [st, py, pl, di] = await Promise.all([
        API.get('/admin/students-with-primary-discipline'),
        API.get('/payers'),
        API.get('/plans'),
        API.get('/disciplines'),
      ]);

      setStudents(st.data || []);
      setPayers(py.data || []);
      setPlans(pl.data || []);
      setDisciplines(di.data || []);
    } catch (e) {
      console.error(e);
      setErr('Failed to load base data.');
    } finally {
      setLoading(false);
    }
  }

  async function loadUsageForAuthList(list) {
    const usage = {};
    await Promise.all(
      (list || []).map(async (a) => {
        try {
          const u = await API.get(`/authorizations/${a.id}/usage`);
          usage[a.id] = u.data;
        } catch {
          usage[a.id] = null;
        }
      })
    );
    return usage;
  }

  async function loadAuths(sid) {
    try {
      setErr('');

      if (!sid) {
        setAuths([]);
        setUsageMap({});
        return;
      }

      // -------- ALL mode --------
      if (sid === ALL) {
        setLoading(true);

        const res = await API.get('/authorizations/all');
        const list = res.data || [];

        const enriched = list.map(a => {
          // BE sada šalje student_label sa PRIMARY discipline
          if (a.student_label) return a;
          const ln = (a.student_last_name || '').trim();
          const fn = (a.student_first_name || '').trim();
          const name = `${ln}${ln ? ', ' : ''}${fn}`.trim() || `Student #${a.student_id}`;
          const primaryCode = a.student_primary_service_type || '—';
          return { ...a, student_label: `${name} (${primaryCode})` };
        });

        const usage = await loadUsageForAuthList(enriched);

        enriched.sort((x, y) => {
          const sl = String(x.student_label || '').localeCompare(String(y.student_label || ''));
          if (sl !== 0) return sl;
          const edx = String(x.end_date || '');
          const edy = String(y.end_date || '');
          if (edx !== edy) return edy.localeCompare(edx);
          return Number(y.id || 0) - Number(x.id || 0);
        });

        setAuths(enriched);
        setUsageMap(usage);
        setLoading(false);
        return;
      }

      // -------- single student mode --------
      setLoading(true);

      const res = await API.get(`/students/${sid}/authorizations`);
      const list = res.data || [];
      setAuths(list);

      const usage = await loadUsageForAuthList(list);
      setUsageMap(usage);

      setLoading(false);
    } catch (e) {
      console.error(e);
      setErr('Failed to load authorizations.');
      setLoading(false);
    }
  }

  async function loadStudentDisciplines(sid) {
    try {
      if (!sid || sid === ALL) {
        setStudentDisciplines([]);
        setStudentPrimary(null);
        return;
      }
      const res = await API.get(`/${sid}/disciplines`);
      const list = res.data || [];
      setStudentDisciplines(list);
      const primary = list.find(x => Number(x.is_primary) === 1) || null;
      setStudentPrimary(primary);
    } catch (e) {
      console.error(e);
      setStudentDisciplines([]);
      setStudentPrimary(null);
    }
  }

  useEffect(() => { loadBase(); }, []);
  useEffect(() => { loadAuths(studentId); }, [studentId]);
  useEffect(() => { loadStudentDisciplines(studentId); }, [studentId]);

  function openNew() {
    if (studentId === ALL) return;

    setServicesError(false);
    setServiceRowErrors({});

    setForm({
      id: null,
      student_id: studentId ? Number(studentId) : null,
      plan_id: plans[0]?.id ? String(plans[0].id) : '',
      auth_number: '',
      start_date: '',
      end_date: '',
      unit_type: 'unit',
      total_units: '0',
      used_units_override: '',
      notes: '',
      status: 'active',
      services: [], // start empty, but MUST add at least one
    });
    setModal(true);
  }

  function openEdit(a) {
    // u ALL modu nema Edit dugmeta (po zahtevu), ali guard ostaje
    if (studentId === ALL) return;

    setServicesError(false);
    setServiceRowErrors({});

    setForm({
      id: a.id,
      student_id: a.student_id,
      plan_id: String(a.plan_id),
      auth_number: a.auth_number || '',
      start_date: String(a.start_date || '').slice(0, 10),
      end_date: String(a.end_date || '').slice(0, 10),
      unit_type: a.unit_type || 'unit',
      total_units: String(a.total_units ?? '0'),
      used_units_override: a.used_units_override === null ? '' : String(a.used_units_override),
      notes: a.notes || '',
      status: a.status || 'active',
      services: Array.isArray(a.services) ? a.services.map(s => ({
        discipline_id: s.discipline_id ? String(s.discipline_id) : '',
        cpt_code: s.cpt_code || '',
        modifier1: s.modifier1 || '',
        modifier2: s.modifier2 || '',
        modifier3: s.modifier3 || '',
        modifier4: s.modifier4 || '',
        place_of_service: s.place_of_service || '',
        units_per_visit_default: s.units_per_visit_default === null ? '' : String(s.units_per_visit_default),
      })) : [],
    });
    setModal(true);
  }

  function addServiceRow() {
    setServicesError(false);
    setForm(f => ({
      ...f,
      services: [
        ...(f.services || []),
        { discipline_id:'', cpt_code:'', modifier1:'', modifier2:'', modifier3:'', modifier4:'', place_of_service:'', units_per_visit_default:'' }
      ],
    }));
  }

  function updateServiceRow(idx, key, value) {
    setForm(f => {
      const next = [...(f.services || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...f, services: next };
    });
    setServiceRowErrors((prev) => {
      const copy = { ...prev };
      if (copy[idx]) {
        copy[idx] = { ...copy[idx], [key]: false };
      }
      return copy;
    });
  }

  function removeServiceRow(idx) {
    setForm(f => {
      const next = [...(f.services || [])];
      next.splice(idx, 1);
      return { ...f, services: next };
    });
    setServiceRowErrors((prev) => {
      const copy = { ...prev };
      delete copy[idx];
      return copy;
    });
  }

  function validateServices() {
    const services = form.services || [];
    const rowErrs = {};

    if (!services.length) {
      setServicesError(true);
      setServiceRowErrors({});
      return false;
    }

    for (let i = 0; i < services.length; i++) {
      const s = services[i] || {};
      const did = String(s.discipline_id || '').trim();
      if (!did) {
        rowErrs[i] = { ...(rowErrs[i] || {}), discipline_id: true };
      }
    }

    setServicesError(false);
    setServiceRowErrors(rowErrs);

    return Object.keys(rowErrs).length === 0;
  }

  async function saveAuth() {
    try {
      setErr('');

      if (!studentId || studentId === ALL) return setErr('Select a specific student first.');

      // ✅ MUST have at least 1 service row + discipline in each row
      if (!validateServices()) {
        return setErr('Services are required (add at least one row, and select discipline).');
      }

      const payload = {
        student_id: Number(studentId),
        plan_id: Number(form.plan_id),
        auth_number: form.auth_number,
        start_date: form.start_date,
        end_date: form.end_date,
        unit_type: form.unit_type,
        total_units: Number(form.total_units || 0),
        used_units_override: form.used_units_override === '' ? null : Number(form.used_units_override),
        notes: form.notes || null,
        status: form.status,
        services: (form.services || []).map(s => ({
          discipline_id: Number(s.discipline_id), // REQUIRED
          cpt_code: s.cpt_code || null,
          modifier1: s.modifier1 || null,
          modifier2: s.modifier2 || null,
          modifier3: s.modifier3 || null,
          modifier4: s.modifier4 || null,
          place_of_service: s.place_of_service || null,
          units_per_visit_default: s.units_per_visit_default === '' ? null : Number(s.units_per_visit_default),
        })),
      };

      if (form.id) await API.put(`/authorizations/${form.id}`, payload);
      else await API.post('/authorizations', payload);

      setModal(false);
      await loadAuths(studentId);
    } catch (e) {
      console.error(e);
      // BE sad vraća konkretne poruke (discipline not allowed, discipline required...)
      const msg = e?.response?.data?.error || 'Failed to save authorization.';
      setErr(msg);
    }
  }

  const canAdd = studentId && studentId !== ALL;
  const showEditInList = studentId && studentId !== ALL; // ✅ remove edit button in ALL list

  return (
    <div className="p-3 md:p-6">
      <div className="rounded-3xl bg-gradient-to-br from-sky-50 via-white to-indigo-50 border border-slate-200 shadow-sm p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-lg md:text-xl font-semibold text-slate-900">Authorizations</div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              disabled={!canAdd}
              onClick={openNew}
              className={cls(
                'px-3 py-2 rounded-xl text-sm border',
                canAdd ? 'bg-sky-600 text-white border-sky-600 hover:bg-sky-700' : 'bg-slate-100 text-slate-400 border-slate-200'
              )}
              title={!canAdd ? 'Select a specific student to add authorization.' : ''}
            >
              + Add Authorization
            </button>
            <button onClick={() => loadAuths(studentId)} className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50">
              Refresh
            </button>
          </div>
        </div>

        {err ? <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div> : null}

        <div className="mt-4">
          <div className="max-w-xl">
            <Select label="Select student" value={studentId} onChange={setStudentId} options={studentOptions} />
          </div>
          {studentId !== ALL && studentPrimary ? (
            <div className="mt-2 text-xs text-slate-600">
              Primary discipline: <span className="font-semibold">{studentPrimary.name}</span>{' '}
              <Pill tone={disciplineToneByServiceType(studentPrimary.service_type)} className="ml-2">
                {studentPrimary.service_type}
              </Pill>
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="mt-4 text-sm text-slate-600">Loading…</div>
        ) : (
          <div className="mt-4 grid gap-3">
            {auths.map((a) => {
              const u = usageMap[a.id];
              const rem = u ? Number(u.remaining_units || 0) : null;

              return (
                <div key={a.id} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-900">
                          Auth <span className="font-mono">{a.auth_number}</span>
                        </div>

                        {a.student_label ? <Pill tone="slate">{a.student_label}</Pill> : null}

                        <Pill tone="indigo">{a.payer_name}</Pill>
                        <Pill tone="sky">{a.plan_name}</Pill>

                        <Pill tone={a.status === 'active' ? 'emerald' : a.status === 'expired' ? 'amber' : 'red'}>{a.status}</Pill>
                        <Pill>{a.unit_type}</Pill>
                        {u ? <Pill tone={remainingTone(rem)}>{rem} remaining</Pill> : <Pill tone="amber">usage?</Pill>}
                      </div>

                      <div className="text-xs text-slate-600 mt-1">
                        {String(a.start_date).slice(0,10)} → {String(a.end_date).slice(0,10)} • total: {a.total_units}
                        {a.used_units_override !== null ? ` • override used: ${a.used_units_override}` : ''}
                      </div>

                      {a.notes ? <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{a.notes}</div> : null}

                      <div className="mt-3">
                        <div className="text-xs font-semibold text-slate-700 mb-1">Services</div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="text-xs text-slate-600">
                                <th className="text-left py-1 pr-3">Discipline</th>
                                <th className="text-left py-1 pr-3">CPT</th>
                                <th className="text-left py-1 pr-3">Mods</th>
                                <th className="text-left py-1 pr-3">POS</th>
                                <th className="text-left py-1 pr-3">Units/visit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(a.services || []).map((s, idx) => {
                                const st = s.discipline_service_type || '';
                                const tone = disciplineToneByServiceType(st);
                                return (
                                  <tr key={idx} className="border-t border-slate-100">
                                    <td className="py-1 pr-3">
                                      {s.discipline_name ? (
                                        <Pill tone={tone}>
                                          {s.discipline_name} {st ? `(${st})` : ''}
                                        </Pill>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className="py-1 pr-3 font-mono">{s.cpt_code || '—'}</td>
                                    <td className="py-1 pr-3 font-mono">
                                      {[s.modifier1, s.modifier2, s.modifier3, s.modifier4].filter(Boolean).join(', ') || '—'}
                                    </td>
                                    <td className="py-1 pr-3 font-mono">{s.place_of_service || '—'}</td>
                                    <td className="py-1 pr-3">{s.units_per_visit_default ?? '—'}</td>
                                  </tr>
                                );
                              })}
                              {!a.services?.length ? (
                                <tr>
                                  <td className="py-2 text-slate-600" colSpan={5}>No services rows.</td>
                                </tr>
                              ) : null}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {showEditInList ? (
                      <div className="shrink-0 flex items-center gap-2">
                        <button
                          className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50"
                          onClick={() => openEdit(a)}
                        >
                          Edit
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {studentId && auths.length === 0 ? (
              <div className="text-sm text-slate-600">
                {studentId === ALL ? 'No students with authorizations yet.' : 'No authorizations for this student yet.'}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={form.id ? 'Edit Authorization' : 'Add Authorization'}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="Plan"
            value={form.plan_id}
            onChange={(v) => setForm(f => ({ ...f, plan_id: v }))}
            options={planOptions}
            hint="Services discipline must match the student's disciplines (and should align with the selected plan)."
          />
          <Input label="Auth number" value={form.auth_number} onChange={(v) => setForm(f => ({ ...f, auth_number: v }))} />
          <Select
            label="Unit type"
            value={form.unit_type}
            onChange={(v) => setForm(f => ({ ...f, unit_type: v }))}
            options={[{ value:'unit', label:'unit' }, { value:'minute', label:'minute' }]}
          />
          <Input label="Start date" type="date" value={form.start_date} onChange={(v)=>setForm(f=>({ ...f, start_date:v }))} />
          <Input label="End date" type="date" value={form.end_date} onChange={(v)=>setForm(f=>({ ...f, end_date:v }))} />
          <Input label="Total" value={form.total_units} onChange={(v)=>setForm(f=>({ ...f, total_units:v }))} />
          <Input label="Used override (optional)" value={form.used_units_override} onChange={(v)=>setForm(f=>({ ...f, used_units_override:v }))} />
          <Select
            label="Status"
            value={form.status}
            onChange={(v)=>setForm(f=>({ ...f, status:v }))}
            options={[{ value:'active', label:'active' }, { value:'expired', label:'expired' }, { value:'void', label:'void' }]}
          />
          <div className="md:col-span-3">
            <Input label="Notes" value={form.notes} onChange={(v)=>setForm(f=>({ ...f, notes:v }))} />
          </div>
        </div>

        {/* ✅ REQUIRED services */}
        <div className={cls(
          'mt-4 rounded-2xl border bg-slate-50 p-3',
          servicesError ? 'border-red-300 bg-red-50' : 'border-slate-200'
        )}>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-800">
              Services <span className="text-red-600">(required)</span>
            </div>
            <button onClick={addServiceRow} className="px-2.5 py-1.5 rounded-lg text-xs bg-white border border-slate-200 hover:bg-slate-50">
              + Add row
            </button>
          </div>

          {servicesError ? (
            <div className="mt-2 text-xs text-red-700">
              You must add at least one service row.
            </div>
          ) : null}

          <div className="mt-3 space-y-3">
            {(form.services || []).map((s, idx) => (
              <div key={idx} className="rounded-2xl bg-white border border-slate-200 p-3">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <Select
                    label="Discipline (required)"
                    value={s.discipline_id}
                    onChange={(v)=>updateServiceRow(idx, 'discipline_id', v)}
                    options={disciplineOptionsForStudent}
                    error={!!serviceRowErrors[idx]?.discipline_id}
                    hint="Only disciplines assigned to this student are shown."
                  />
                  <Input label="CPT" value={s.cpt_code} onChange={(v)=>updateServiceRow(idx, 'cpt_code', v)} placeholder="e.g. 97110" />
                  <Input label="Mod1" value={s.modifier1} onChange={(v)=>updateServiceRow(idx, 'modifier1', v)} />
                  <Input label="Mod2" value={s.modifier2} onChange={(v)=>updateServiceRow(idx, 'modifier2', v)} />
                  <Input label="POS" value={s.place_of_service} onChange={(v)=>updateServiceRow(idx, 'place_of_service', v)} placeholder="e.g. 12" />
                  <Input label="Units/visit" value={s.units_per_visit_default} onChange={(v)=>updateServiceRow(idx, 'units_per_visit_default', v)} placeholder="e.g. 3" />
                  <Input label="Mod3" value={s.modifier3} onChange={(v)=>updateServiceRow(idx, 'modifier3', v)} />
                  <Input label="Mod4" value={s.modifier4} onChange={(v)=>updateServiceRow(idx, 'modifier4', v)} />
                </div>

                <div className="mt-3 flex justify-end">
                  <button onClick={() => removeServiceRow(idx)} className="px-2.5 py-1.5 rounded-lg text-xs border border-red-200 text-red-700 hover:bg-red-50">
                    Remove row
                  </button>
                </div>
              </div>
            ))}

            {!form.services?.length ? (
              <div className="text-sm text-slate-600">
                No services rows yet. Click <span className="font-semibold">+ Add row</span> to add the required service.
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setModal(false)} className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={saveAuth} className="px-3 py-2 rounded-xl bg-sky-600 text-white text-sm hover:bg-sky-700">
            Save
          </button>
        </div>
      </Modal>
    </div>
  );
}



